import React, { useState, useEffect } from 'react';
import { orchestratorApi } from '../api/orchestrator';

interface Entity {
  id: string;
  name: string;
  phone: string;
  status: string;
  tenant_id?: string;
  timezone?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  last_activity?: string;
}

interface EntityManagementProps {
  onEntityUpdated?: () => void;
}

export const EntityManagement: React.FC<EntityManagementProps> = ({ onEntityUpdated }) => {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingEntity, setEditingEntity] = useState<Entity | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    loadAllEntities();
  }, []);

  const loadAllEntities = async () => {
    setLoading(true);
    setError(null);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();

      // Load active entities
      const activeResponse = await fetch('/api/entities', { headers: authHeaders });
      const activeData = await activeResponse.json();

      // Load archived entities
      const archivedResponse = await fetch('/api/entities/archived', { headers: authHeaders });
      const archivedData = await archivedResponse.json();

      if (activeData.ok && archivedData.success) {
        const allEntities = [
          ...(activeData.entities || []).map((e: any) => ({ ...e, is_active: true })),
          ...(archivedData.entities || []).map((e: any) => ({ ...e, is_active: false }))
        ];
        setEntities(allEntities);
      } else {
        throw new Error('Failed to load entities');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load entities');
      console.error('Failed to load entities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entity: Entity) => {
    setEditingEntity(entity);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (updatedEntity: Partial<Entity>) => {
    if (!editingEntity) return;

    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/entities/${editingEntity.id}`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedEntity)
      });

      const data = await response.json();
      if (data.ok) {
        await loadAllEntities();
        setShowEditModal(false);
        setEditingEntity(null);
        onEntityUpdated?.();
      } else {
        alert(`Failed to update entity: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to update entity:', error);
      alert('Failed to update entity. Please try again.');
    }
  };

  const handleDelete = async (entity: Entity) => {
    const action = entity.is_active ? 'archive' : 'permanently delete';
    const confirmMessage = entity.is_active
      ? `Archive "${entity.name}"?\n\nThis will:\n✓ Remove it from active sessions\n✓ Preserve all chat history and data\n✓ Allow you to restore it later\n\nThe session will be archived, not permanently deleted.`
      : `Permanently delete "${entity.name}"?\n\n⚠️ WARNING: This is PERMANENT and CANNOT be undone!\n\nThis will delete:\n✓ All chat history\n✓ All messages\n✓ All broadcast campaigns\n✓ All entity data\n\nType "DELETE" to confirm.`;

    if (entity.is_active) {
      if (!window.confirm(confirmMessage)) return;
    } else {
      const confirmation = window.prompt(confirmMessage);
      if (confirmation !== 'DELETE') {
        alert('Deletion cancelled. You must type "DELETE" to confirm.');
        return;
      }
    }

    try {
      const result = entity.is_active
        ? await orchestratorApi.deleteEntity(entity.id)
        : await orchestratorApi.permanentlyDeleteEntity(entity.id);

      if (result.success) {
        await loadAllEntities();
        onEntityUpdated?.();
      } else {
        alert(`Failed to ${action} entity: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(`Failed to ${action} entity:`, error);
      alert(`Failed to ${action} entity. Please try again.`);
    }
  };

  const handleRestore = async (entity: Entity) => {
    if (!window.confirm(`Restore "${entity.name}"?\n\nThis will make it available as an active session again.`)) {
      return;
    }

    try {
      const result = await orchestratorApi.restoreEntity(entity.id);
      if (result.success) {
        await loadAllEntities();
        onEntityUpdated?.();
      } else {
        alert(`Failed to restore entity: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to restore entity:', error);
      alert('Failed to restore entity. Please try again.');
    }
  };

  const filteredEntities = entities.filter(entity => {
    const matchesFilter =
      filter === 'all' ? true :
      filter === 'active' ? entity.is_active :
      !entity.is_active;

    const matchesSearch =
      searchTerm === '' ||
      entity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entity.phone.includes(searchTerm) ||
      entity.id.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return '🟢';
      case 'disconnected': return '🔴';
      case 'authenticating': return '🟡';
      case 'needs_qr': return '🟠';
      case 'error': return '⚠️';
      default: return '⚪';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div className="loading">Loading entities...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div className="error-message" style={{ color: 'red', padding: '10px', backgroundColor: '#fee', borderRadius: '4px' }}>
          ❌ {error}
        </div>
        <button
          onClick={loadAllEntities}
          style={{ marginTop: '10px' }}
          className="btn btn-primary"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="entity-management-container" style={{ padding: '20px' }}>
      <div className="entity-management-header" style={{ marginBottom: '20px' }}>
        <h2>🏢 Entity Management</h2>
        <p className="section-description">
          Manage all WhatsApp Business entities in your system. View, edit, archive, and restore entities.
        </p>
      </div>

      <div className="entity-management-controls" style={{
        display: 'flex',
        gap: '15px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Filter Buttons */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('all')}
          >
            All ({entities.length})
          </button>
          <button
            className={`btn ${filter === 'active' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('active')}
          >
            Active ({entities.filter(e => e.is_active).length})
          </button>
          <button
            className={`btn ${filter === 'inactive' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('inactive')}
          >
            Archived ({entities.filter(e => !e.is_active).length})
          </button>
        </div>

        {/* Search */}
        <div style={{ flex: 1, minWidth: '250px' }}>
          <input
            type="text"
            placeholder="🔍 Search by name, phone, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '14px'
            }}
          />
        </div>

        {/* Refresh Button */}
        <button
          className="btn btn-secondary"
          onClick={loadAllEntities}
          title="Refresh entity list"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Entities Table */}
      <div style={{ overflowX: 'auto' }}>
        <table className="entity-table" style={{
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Name</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Phone / ID</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Tenant</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Timezone</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Created</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Last Activity</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Active</th>
              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEntities.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  {searchTerm ? '🔍 No entities found matching your search.' : '📭 No entities found.'}
                </td>
              </tr>
            ) : (
              filteredEntities.map(entity => (
                <tr
                  key={entity.id}
                  style={{
                    borderBottom: '1px solid #dee2e6',
                    opacity: entity.is_active ? 1 : 0.6,
                    backgroundColor: entity.is_active ? 'white' : '#f8f9fa'
                  }}
                >
                  <td style={{ padding: '12px' }}>
                    <span title={entity.status} style={{ fontSize: '20px' }}>
                      {getStatusIcon(entity.status)}
                    </span>
                  </td>
                  <td style={{ padding: '12px', fontWeight: 500 }}>
                    {entity.name}
                  </td>
                  <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '13px' }}>
                    <div>{entity.phone}</div>
                    <div style={{ fontSize: '11px', color: '#666' }}>{entity.id}</div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '2px 8px',
                      backgroundColor: entity.tenant_id ? '#e3f2fd' : '#fee',
                      color: entity.tenant_id ? '#1976d2' : '#d32f2f',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontFamily: 'monospace'
                    }}>
                      {entity.tenant_id || 'No tenant'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', fontSize: '13px' }}>
                    {entity.timezone || 'N/A'}
                  </td>
                  <td style={{ padding: '12px', fontSize: '12px', color: '#666' }}>
                    {formatDate(entity.created_at)}
                  </td>
                  <td style={{ padding: '12px', fontSize: '12px', color: '#666' }}>
                    {formatDate(entity.last_activity)}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {entity.is_active ? (
                      <span style={{ color: 'green', fontWeight: 500 }}>✓ Yes</span>
                    ) : (
                      <span style={{ color: '#999', fontWeight: 500 }}>✗ No</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      {entity.is_active ? (
                        <>
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleEdit(entity)}
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                            title="Edit entity"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => handleDelete(entity)}
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                            title="Archive entity"
                          >
                            📦 Archive
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn-success"
                            onClick={() => handleRestore(entity)}
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                            title="Restore entity"
                          >
                            ♻️ Restore
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => handleDelete(entity)}
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                            title="Permanently delete entity"
                          >
                            🗑️ Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingEntity && (
        <EditEntityModal
          entity={editingEntity}
          onSave={handleSaveEdit}
          onClose={() => {
            setShowEditModal(false);
            setEditingEntity(null);
          }}
        />
      )}
    </div>
  );
};

interface EditEntityModalProps {
  entity: Entity;
  onSave: (updated: Partial<Entity>) => void;
  onClose: () => void;
}

const EditEntityModal: React.FC<EditEntityModalProps> = ({ entity, onSave, onClose }) => {
  const [name, setName] = useState(entity.name);
  const [phone, setPhone] = useState(entity.phone);
  const [timezone, setTimezone] = useState(entity.timezone || 'Asia/Singapore');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ name, phone, timezone });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          minWidth: '500px',
          maxWidth: '600px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>✏️ Edit Entity</h3>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
              Entity ID (Phone)
            </label>
            <input
              type="text"
              value={entity.id}
              disabled
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                backgroundColor: '#f5f5f5',
                fontFamily: 'monospace'
              }}
            />
            <small style={{ color: '#666', fontSize: '12px' }}>Cannot be changed</small>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ddd'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
              Phone Number *
            </label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              placeholder="+65 9123 4567"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                fontFamily: 'monospace'
              }}
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              International format (e.g., +65 9123 4567)
            </small>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
              Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ddd'
              }}
            >
              <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
              <option value="Asia/Kuala_Lumpur">Asia/Kuala_Lumpur (MYT)</option>
              <option value="Asia/Bangkok">Asia/Bangkok (ICT)</option>
              <option value="Asia/Jakarta">Asia/Jakarta (WIB)</option>
              <option value="Asia/Manila">Asia/Manila (PHT)</option>
              <option value="Asia/Hong_Kong">Asia/Hong_Kong (HKT)</option>
              <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
              <option value="Australia/Sydney">Australia/Sydney (AEDT)</option>
              <option value="Europe/London">Europe/London (GMT)</option>
              <option value="America/New_York">America/New_York (EST)</option>
              <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={saving}
              style={{ padding: '8px 16px' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              style={{ padding: '8px 16px' }}
            >
              {saving ? 'Saving...' : '💾 Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
